import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useModal } from '../../components/modal/ModalContext';
import AlertModal from '../../components/modal/AlertModal';
import ConfirmModal from '../../components/modal/ConfirmModal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const formatPence = (pence: number): string =>
  `\u00a3${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const penceToPounds = (pence: number): string => (pence / 100).toFixed(2);
const poundsToPence = (pounds: string): number => Math.round(parseFloat(pounds || '0') * 100);

interface CurrentTier {
  id: number;
  min_companies: number;
  max_companies: number;
  fee_pence: number;
  label: string;
}

interface Settlement {
  settlement_month: string;
  active_company_count: number;
  gross_revenue_pence: number;
  operating_fee_tier: string;
  operating_fee_pence: number;
  platform_base_fee_pence: number;
  hosting_cost_pence: number;
  stripe_fee_pence: number;
  ai_cost_pence: number;
  other_cost_pence: number;
  topup_gross_pence?: number;
  topup_infrastructure_fee_pence?: number;
  total_variable_cost_pence: number;
  total_cost_pence: number;
  cost_breakdown_json: string | null;
  net_distributable_pence: number;
  eis_share_pct: number;
  yiontech_share_pct: number;
  eis_share_pence: number;
  yiontech_share_pence: number;
  status: 'draft' | 'final' | 'invoiced' | 'paid';
  finalized_at: string | null;
  invoice_url: string | null;
}

interface MonthlyCosts {
  hosting_cost_pence: number;
  stripe_fee_pence: number;
  ai_cost_pence: number;
  other_cost_pence: number;
}

interface RevenueEvent {
  id: number;
  event_type: string;
  amount_pence: number;
  currency: string;
  description: string;
  created_at: string;
  company_name?: string;
  partner_name?: string;
}

interface DashboardData {
  current_month: string;
  active_companies: number;
  current_tier: CurrentTier | null;
  current_tier_label: string;
  current_tier_fee: string;
  current_tier_fee_pence: number;
  mtd_revenue: string;
  mtd_revenue_pence: number;
  mtd_events: number;
  is_developer_admin: boolean;
  latest_settlement: Settlement | null;
  monthly_costs: MonthlyCosts | null;
  history: Settlement[];
}

export default function SettlementDashboard() {
  const { openModal } = useModal();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [history, setHistory] = useState<Settlement[]>([]);
  const [revenueEvents, setRevenueEvents] = useState<RevenueEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [calcMonth, setCalcMonth] = useState('');

  // Developer costs inline form
  const [costHosting, setCostHosting] = useState('0.00');
  const [costStripe, setCostStripe] = useState('0.00');
  const [costAi, setCostAi] = useState('0.00');
  const [costOther, setCostOther] = useState('0.00');
  const [costNotes, setCostNotes] = useState('');
  const [savingCosts, setSavingCosts] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch('/api/billing/admin/settlement/dashboard', { headers: authHeaders() });
      const d = await res.json();
      setDashboard(d);
      if (d.history) setHistory(d.history);
      if (!calcMonth && d.current_month) {
        setCalcMonth(d.current_month);
      }
      // Populate cost form from monthly_costs if available
      if (d.monthly_costs) {
        setCostHosting(penceToPounds(d.monthly_costs.hosting_cost_pence || 0));
        setCostStripe(penceToPounds(d.monthly_costs.stripe_fee_pence || 0));
        setCostAi(penceToPounds(d.monthly_costs.ai_cost_pence || 0));
        setCostOther(penceToPounds(d.monthly_costs.other_cost_pence || 0));
      }
    } catch { /* silent */ }
  }, [calcMonth]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch('/api/billing/admin/settlement/history', { headers: authHeaders() });
      const d = await res.json();
      if (Array.isArray(d)) {
        setHistory(d);
      } else if (d.settlements) {
        setHistory(d.settlements);
      }
    } catch { /* silent */ }
  }, []);

  const fetchRevenueEvents = useCallback(async () => {
    try {
      const res = await fetch('/api/billing/admin/revenue-events', { headers: authHeaders() });
      const d = await res.json();
      if (d.events) setRevenueEvents(d.events);
      else if (Array.isArray(d)) setRevenueEvents(d);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchDashboard(), fetchHistory(), fetchRevenueEvents()]).finally(() => setLoading(false));
  }, [fetchDashboard, fetchHistory, fetchRevenueEvents]);

  const handleCalculate = async () => {
    if (!calcMonth) return;
    setCalculating(true);
    try {
      const res = await fetch('/api/billing/admin/settlement/calculate', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ month: calcMonth }),
      });
      const d = await res.json();
      if (d.success || d.settlement_month) {
        openModal({
          component: AlertModal,
          props: {
            title: 'Settlement Calculated',
            message: `Settlement for ${calcMonth} has been calculated successfully.`,
            type: 'success',
          },
        });
        await Promise.all([fetchDashboard(), fetchHistory()]);
      } else {
        openModal({
          component: AlertModal,
          props: {
            title: 'Calculation Failed',
            message: d.error || 'Failed to calculate settlement.',
            type: 'error',
          },
        });
      }
    } catch {
      openModal({
        component: AlertModal,
        props: { title: 'Error', message: 'Network error calculating settlement.', type: 'error' },
      });
    } finally {
      setCalculating(false);
    }
  };

  const handleFinalize = (month: string) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Finalize Settlement',
        message: `Finalizing settlement for ${month} makes it immutable. Once finalized, the revenue split will be locked permanently. Proceed?`,
        confirmText: 'Finalize Settlement',
        cancelText: 'Cancel',
        type: 'warning',
        loading: finalizing,
        onConfirm: async () => {
          setFinalizing(true);
          try {
            const res = await fetch('/api/billing/admin/settlement/finalize', {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ month }),
            });
            const d = await res.json();
            if (d.success) {
              openModal({
                component: AlertModal,
                props: {
                  title: 'Settlement Finalized',
                  message: `Settlement for ${month} has been finalized and locked.`,
                  type: 'success',
                },
              });
              await Promise.all([fetchDashboard(), fetchHistory()]);
            } else {
              openModal({
                component: AlertModal,
                props: {
                  title: 'Finalization Failed',
                  message: d.error || 'Failed to finalize settlement.',
                  type: 'error',
                },
              });
            }
          } catch {
            openModal({
              component: AlertModal,
              props: { title: 'Error', message: 'Network error finalizing settlement.', type: 'error' },
            });
          } finally {
            setFinalizing(false);
          }
        },
      },
      options: { size: 'small' },
    });
  };

  const handleGenerateInvoice = async (month: string) => {
    try {
      const res = await fetch(`/api/billing/developer/settlement/${month}/generate-invoice`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        const html = await res.text();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        const d = await res.json().catch(() => ({ error: 'Failed to generate invoice.' }));
        openModal({
          component: AlertModal,
          props: { title: 'Invoice Error', message: d.error || 'Failed to generate invoice.', type: 'error' },
        });
      }
    } catch {
      openModal({
        component: AlertModal,
        props: { title: 'Error', message: 'Network error generating invoice.', type: 'error' },
      });
    }
  };

  const handleSaveCosts = async () => {
    if (!dashboard?.current_month) return;
    setSavingCosts(true);
    try {
      const res = await fetch('/api/billing/developer/costs', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          month: dashboard.current_month,
          hosting_cost_pence: poundsToPence(costHosting),
          stripe_fee_pence: poundsToPence(costStripe),
          ai_cost_pence: poundsToPence(costAi),
          other_cost_pence: poundsToPence(costOther),
          notes: costNotes,
        }),
      });
      const d = await res.json();
      if (d.success !== false) {
        openModal({
          component: AlertModal,
          props: { title: 'Costs Saved', message: `Monthly costs for ${dashboard.current_month} have been saved.`, type: 'success' },
        });
        await fetchDashboard();
      } else {
        openModal({
          component: AlertModal,
          props: { title: 'Save Failed', message: d.error || 'Failed to save costs.', type: 'error' },
        });
      }
    } catch {
      openModal({
        component: AlertModal,
        props: { title: 'Error', message: 'Network error saving costs.', type: 'error' },
      });
    } finally {
      setSavingCosts(false);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      final: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      invoiced: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  const latest = dashboard?.latest_settlement ?? null;
  const isDev = dashboard?.is_developer_admin ?? false;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Platform Settlement Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Hybrid cost model: Gross Revenue &minus; Platform Base Fee &minus; Variable Costs = Net Revenue (80/20 split)
          </p>
        </div>
        {isDev && (
          <Link
            to="/admin/developer-billing"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            Developer Settings
          </Link>
        )}
      </div>

      {/* Overview Cards - 9 card grid */}
      {dashboard && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <OverviewCard
            label="Current Period"
            value={dashboard.current_month}
            sublabel="Settlement month"
            accent="slate"
          />
          <OverviewCard
            label="Active Companies"
            value={String(dashboard.active_companies)}
            sublabel="With active subscriptions"
            accent="blue"
            badge={dashboard.current_tier_label}
          />
          <OverviewCard
            label="Gross Revenue"
            value={formatPence(dashboard.mtd_revenue_pence)}
            sublabel={`${dashboard.mtd_events} revenue events`}
            accent="emerald"
          />
          <OverviewCard
            label="Platform Base Fee"
            value={formatPence(dashboard.current_tier_fee_pence)}
            sublabel="Current tier monthly fee"
            accent="amber"
            badge={dashboard.current_tier?.label}
          />
          <OverviewCard
            label="Variable Costs"
            value={latest ? formatPence(latest.total_variable_cost_pence) : '\u00a30.00'}
            sublabel="Hosting + Stripe + AI + Other"
            accent="orange"
          />
          <OverviewCard
            label="Total Cost"
            value={latest ? formatPence(latest.total_cost_pence) : '\u00a30.00'}
            sublabel="Base Fee + Variable Costs"
            accent="red"
          />
          <OverviewCard
            label="Net Revenue"
            value={latest ? formatPence(latest.net_distributable_pence) : '\u00a30.00'}
            sublabel="Gross - Total Cost"
            accent="indigo"
          />
          <OverviewCard
            label="EIS Share (80%)"
            value={latest ? formatPence(latest.eis_share_pence) : '\u00a30.00'}
            sublabel="Net Revenue x 80%"
            accent="emerald"
          />
          <OverviewCard
            label="Yiontech Share (20%)"
            value={latest ? formatPence(latest.yiontech_share_pence) : '\u00a30.00'}
            sublabel="Net Revenue x 20%"
            accent="blue"
          />
        </div>
      )}

      {/* Cost Breakdown - Developer admin only */}
      {isDev && latest && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Cost Breakdown: {latest.settlement_month}
            </h2>
            {statusBadge(latest.status)}
          </div>

          <div className="bg-slate-50 dark:bg-slate-700/40 rounded-lg px-4 py-3 border border-slate-200 dark:border-slate-600 mb-6">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
              Gross Revenue &minus; Platform Base Fee &minus; Variable Costs = Net Revenue &rarr; 80/20 Split
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <BreakdownRow
                label={`Platform Base Fee (${latest.operating_fee_tier || 'Tier'})`}
                value={formatPence(latest.platform_base_fee_pence)}
                bold
              />
              <div className="border-t border-slate-200 dark:border-slate-600 pt-2 mt-2">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Variable Costs</p>
                <BreakdownRow label="Stripe Processing Fees" value={formatPence(latest.stripe_fee_pence)} />
                <BreakdownRow label="Hosting & Infrastructure" value={formatPence(latest.hosting_cost_pence)} />
                <BreakdownRow label="AI Provider Costs" value={formatPence(latest.ai_cost_pence)} />
                <BreakdownRow label="Other Costs" value={formatPence(latest.other_cost_pence)} />
              </div>
              <div className="border-t border-slate-200 dark:border-slate-600 pt-2">
                <BreakdownRow label="Total Variable Costs" value={formatPence(latest.total_variable_cost_pence)} bold />
              </div>
              <div className="border-t border-slate-300 dark:border-slate-500 pt-2">
                <BreakdownRow label="Total Cost (Base + Variable)" value={formatPence(latest.total_cost_pence)} bold highlight="red" />
              </div>
            </div>

            <div className="space-y-2">
              <BreakdownRow label="Gross Revenue" value={formatPence(latest.gross_revenue_pence)} bold />
              <BreakdownRow label="Total Cost" value={`-${formatPence(latest.total_cost_pence)}`} highlight="red" />
              <div className="border-t border-slate-200 dark:border-slate-600 pt-2">
                <BreakdownRow label="Net Distributable Revenue" value={formatPence(latest.net_distributable_pence)} bold />
              </div>
              {(latest.topup_infrastructure_fee_pence ?? 0) > 0 && (
                <div className="border-t border-slate-200 dark:border-slate-600 pt-2">
                  <BreakdownRow
                    label="AI Infrastructure Service Fee"
                    value={`-${formatPence(latest.topup_infrastructure_fee_pence!)}`}
                    highlight="slate"
                  />
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Per SLA: portion of overage (topup) revenue routed to the AI infrastructure provider, recognising the
                    additional Anthropic usage and engineering that overage traffic requires.
                  </p>
                </div>
              )}
              <div className="border-t border-slate-200 dark:border-slate-600 pt-2">
                <BreakdownRow
                  label={`EIS Share (${latest.eis_share_pct ?? 80}% of remainder)`}
                  value={formatPence(latest.eis_share_pence)}
                  highlight="emerald"
                  bold
                />
                <BreakdownRow
                  label={`Yiontech Share (${latest.yiontech_share_pct ?? 20}% of remainder${(latest.topup_infrastructure_fee_pence ?? 0) > 0 ? ' + infra fee' : ''})`}
                  value={formatPence(latest.yiontech_share_pence)}
                  highlight="violet"
                  bold
                />
              </div>
              {latest.finalized_at && (
                <p className="text-xs text-slate-500 dark:text-slate-400 pt-3">
                  Finalized: {new Date(latest.finalized_at).toLocaleString('en-GB')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Calculate Settlement */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Calculate Settlement</h2>
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Settlement Month</label>
            <input
              type="month"
              value={calcMonth}
              onChange={(e) => setCalcMonth(e.target.value)}
              className="block w-48 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleCalculate}
            disabled={calculating || !calcMonth}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 shadow-sm"
          >
            {calculating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Calculating...
              </>
            ) : (
              'Calculate Current Month'
            )}
          </button>
        </div>
      </div>

      {/* Settlement History Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Settlement History</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Month</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Companies</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Gross</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Base Fee</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Variable</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Total Cost</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Net</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">EIS (80%)</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Yiontech (20%)</th>
                <th className="text-center py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Status</th>
                <th className="text-center py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-slate-400 dark:text-slate-500">
                    No settlement records yet. Calculate a month to get started.
                  </td>
                </tr>
              ) : (
                history.map((s) => (
                  <tr
                    key={s.settlement_month}
                    className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                  >
                    <td className="py-3 px-3 font-medium text-slate-900 dark:text-white">{s.settlement_month}</td>
                    <td className="py-3 px-3 text-right text-slate-700 dark:text-slate-300">{s.active_company_count}</td>
                    <td className="py-3 px-3 text-right text-slate-700 dark:text-slate-300">{formatPence(s.gross_revenue_pence)}</td>
                    <td className="py-3 px-3 text-right text-slate-700 dark:text-slate-300">{formatPence(s.platform_base_fee_pence)}</td>
                    <td className="py-3 px-3 text-right text-slate-700 dark:text-slate-300">{formatPence(s.total_variable_cost_pence)}</td>
                    <td className="py-3 px-3 text-right font-medium text-slate-900 dark:text-white">{formatPence(s.total_cost_pence)}</td>
                    <td className="py-3 px-3 text-right font-medium text-slate-900 dark:text-white">{formatPence(s.net_distributable_pence)}</td>
                    <td className="py-3 px-3 text-right text-emerald-600 dark:text-emerald-400">{formatPence(s.eis_share_pence)}</td>
                    <td className="py-3 px-3 text-right text-blue-600 dark:text-blue-400">{formatPence(s.yiontech_share_pence)}</td>
                    <td className="py-3 px-3 text-center">{statusBadge(s.status)}</td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {s.status === 'draft' && (
                          <button
                            onClick={() => handleFinalize(s.settlement_month)}
                            disabled={finalizing}
                            className="inline-flex items-center px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                          >
                            Finalize
                          </button>
                        )}
                        {isDev && (
                          <button
                            onClick={() => handleGenerateInvoice(s.settlement_month)}
                            className="inline-flex items-center px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            Invoice
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Developer Controls - only for developer admin */}
      {isDev && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Developer Controls</h2>
            <Link
              to="/admin/developer-billing"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Open Full Developer Settings &rarr;
            </Link>
          </div>

          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Enter monthly variable costs for <span className="font-medium text-slate-700 dark:text-slate-300">{dashboard?.current_month}</span>. These will be used in the next settlement calculation.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Hosting (&pound;)</label>
              <input
                type="number"
                step="0.01"
                value={costHosting}
                onChange={(e) => setCostHosting(e.target.value)}
                className="block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Stripe Fees (&pound;)</label>
              <input
                type="number"
                step="0.01"
                value={costStripe}
                onChange={(e) => setCostStripe(e.target.value)}
                className="block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">AI Costs (&pound;)</label>
              <input
                type="number"
                step="0.01"
                value={costAi}
                onChange={(e) => setCostAi(e.target.value)}
                className="block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Other (&pound;)</label>
              <input
                type="number"
                step="0.01"
                value={costOther}
                onChange={(e) => setCostOther(e.target.value)}
                className="block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Notes</label>
            <input
              type="text"
              value={costNotes}
              onChange={(e) => setCostNotes(e.target.value)}
              placeholder="Optional notes about this month's costs..."
              className="block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleSaveCosts}
              disabled={savingCosts}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 shadow-sm"
            >
              {savingCosts ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                'Save Monthly Costs'
              )}
            </button>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Total: <span className="font-semibold text-slate-900 dark:text-white">
                {formatPence(poundsToPence(costHosting) + poundsToPence(costStripe) + poundsToPence(costAi) + poundsToPence(costOther))}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Revenue Events Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Revenue Events</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Date</th>
                <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Type</th>
                <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Description</th>
                <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Amount</th>
              </tr>
            </thead>
            <tbody>
              {revenueEvents.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-slate-400 dark:text-slate-500">
                    No revenue events for this period.
                  </td>
                </tr>
              ) : (
                revenueEvents.map((ev) => (
                  <tr
                    key={ev.id}
                    className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                  >
                    <td className="py-3 px-3 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                      {new Date(ev.created_at).toLocaleString('en-GB')}
                    </td>
                    <td className="py-3 px-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        {ev.event_type}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-slate-700 dark:text-slate-300">{ev.description}</td>
                    <td className="py-3 px-3 text-right font-medium text-emerald-600 dark:text-emerald-400">
                      {formatPence(ev.amount_pence)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* --- Sub-components --- */

function OverviewCard({
  label,
  value,
  sublabel,
  accent,
  badge,
}: {
  label: string;
  value: string;
  sublabel: string;
  accent: string;
  badge?: string;
}) {
  const accentColors: Record<string, string> = {
    slate: 'border-t-slate-400',
    blue: 'border-t-blue-500',
    emerald: 'border-t-emerald-500',
    amber: 'border-t-amber-500',
    indigo: 'border-t-indigo-500',
    violet: 'border-t-violet-500',
    orange: 'border-t-orange-500',
    red: 'border-t-red-500',
  };

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 border-t-4 ${accentColors[accent] || 'border-t-slate-400'} p-4`}>
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-slate-900 dark:text-white mt-1 truncate">{value}</p>
      <div className="flex items-center gap-2 mt-1">
        <p className="text-xs text-slate-400 dark:text-slate-500">{sublabel}</p>
        {badge && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 max-w-[140px] truncate">
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

function BreakdownRow({
  label,
  value,
  bold,
  highlight,
}: {
  label: string;
  value: string;
  bold?: boolean;
  highlight?: 'emerald' | 'violet' | 'red';
}) {
  const valueColor =
    highlight === 'emerald'
      ? 'text-emerald-600 dark:text-emerald-400'
      : highlight === 'violet'
        ? 'text-violet-600 dark:text-violet-400'
        : highlight === 'red'
          ? 'text-red-500 dark:text-red-400'
          : 'text-slate-900 dark:text-white';

  return (
    <div className="flex items-center justify-between py-1">
      <span className={`text-sm ${bold ? 'font-semibold' : ''} text-slate-600 dark:text-slate-400`}>{label}</span>
      <span className={`text-sm ${bold ? 'font-bold' : 'font-medium'} ${valueColor}`}>{value}</span>
    </div>
  );
}
